import React, { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';

// PublicRoute - No authentication required, but try silent auth
export const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const auth = useAuth();
    const hasAttemptedSilentAuth = useRef(false);
    
    useEffect(() => {
        // Try silent authentication for returning users, but only once
        if (!auth.isLoading && !auth.isAuthenticated && !hasAttemptedSilentAuth.current) {
            hasAttemptedSilentAuth.current = true;
            console.log('PublicRoute: Attempting silent authentication...');
            auth.signinSilent().catch(() => {
                // Silent auth failed, but don't redirect - user can still browse
                console.log('PublicRoute: Silent auth failed, user browsing as guest');
            });
        }
    }, [auth.isLoading, auth.isAuthenticated]);
    
    return <>{children}</>;
};

// ProtectedRoute - Authentication required
export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const auth = useAuth();
    const navigate = useNavigate();
    
    useEffect(() => {
        if (!auth.isLoading && !auth.isAuthenticated) {
            // Store where they were trying to go
            localStorage.setItem('redirectURL', window.location.pathname);
            navigate('/login/handler');
        }
    }, [auth.isLoading, auth.isAuthenticated]);
    
    if (auth.isLoading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                gap: 2
            }}>
                <CircularProgress />
                <Typography variant="body1">Authenticating...</Typography>
            </Box>
        );
    }
    
    if (!auth.isAuthenticated) {
        return null; // Will redirect to login
    }
    
    return <>{children}</>;
};

// LoadingSpinner component for reuse
export const LoadingSpinner: React.FC = () => (
    <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        gap: 2
    }}>
        <CircularProgress />
        <Typography variant="body1">Loading...</Typography>
    </Box>
);
